import { userAtom } from '@/atoms/user.atom';
import { getStoredUser } from '@/core/token';
import { ApiError, execute } from '@/graphql/execute';
import { meQuery } from '@/queries/me.query';
import { useAtom } from 'jotai';
import i18n from '@/core/i18n';
import { analytics } from '@/lib/analytics';
import posthog from 'posthog-js';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

const WHITELIST = ['/auth', '/shared'];

/**
 * Should a failed `me` query log the user out?
 *
 * Only a rejected credential (401/403) should. Everything else — a throttled
 * request, a 5xx, an offline cold launch, a dropped connection — used to bounce
 * the user to /auth/login while their cookie was still perfectly valid: they'd
 * land on the login page "signed out", and re-entering the URL by hand would
 * work. With a token still in hand we keep the cached identity and let the next
 * query retry.
 */
export const shouldEndSession = (error: unknown, storedUser: unknown): boolean => {
  if (!storedUser) return true;
  return error instanceof ApiError && error.isAuthError;
};

let featurebaseChangelogInitialized = false;

const initFeaturebaseChangelogWidget = () => {
  if (featurebaseChangelogInitialized) return;

  // @ts-ignore
  const featurebase = window.Featurebase;
  if (!featurebase) return;

  featurebase('init_changelog_widget', {
    organization: 'skygis',
    changelogCard: {
      enabled: true,
    },
    dropdown: {
      enabled: true,
      placement: 'right',
    },
    popup: {
      enabled: true,
      autoOpenForNewUpdates: true,
    },
    theme: 'light',
    locale: 'en',
  });

  featurebaseChangelogInitialized = true;
};

export const useMe = () => {
  const navigate = useNavigate();

  const [me, setUser] = useAtom(userAtom);

  const _handleRedirectToLogin = useCallback(
    (location: any) => {
      if (WHITELIST.some((path) => location.pathname.startsWith(path))) return;
      navigate('/auth/login', { replace: true });
    },
    [navigate],
  );
  const _handleLoadUser = useCallback(async () => {
    try {
      const data = await execute(meQuery);
      const user = data?.me;
      if (user) {
        setUser(user);

        // Apply user's language preference
        if (user.language) {
          void i18n.changeLanguage(user.language);
        }

        analytics.identify(user.id, {
          email: user.email,
          name: user.name,
        });
        posthog.setPersonPropertiesForFlags({ email: user.email, name: user.name });

        if (user?.featurebaseUser) {
          // @ts-ignore
          window.Featurebase?.('boot', {
            appId: '698a4a6819240b6e36d65566',
            featurebaseJwt: user.featurebaseUser.featurebaseJwt,
            theme: 'light', // Choose between dark or light theme
            locale: 'en', // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
            hideDefaultLauncher: true,
          });

          initFeaturebaseChangelogWidget();

          // @ts-ignore
          window.Featurebase?.('identify', user.featurebaseUser);
        }
      } else throw new Error('User not found');
    } catch (error) {
      const stored = getStoredUser();
      if (stored && !shouldEndSession(error, stored)) {
        setUser(stored);
        if (stored.language) void i18n.changeLanguage(stored.language);
        return;
      }
      _handleRedirectToLogin(window.location);
    }
  }, []);

  const _handleUser = useCallback(async () => {
    const location = window.location;
    const localUser = getStoredUser();
    if (!localUser) _handleRedirectToLogin(location);
    else _handleLoadUser();
  }, []);

  return { me, loadMe: _handleUser };
};
